import { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowRight, ShieldCheck, X, Wallet, QrCode } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { useConfig } from "@/context/ConfigContext";
import { pricePreview, tokenLivePrice, contribute, contributeOnchain, createPaymentIntent } from "@/lib/api";
import {
  sendEvmTokenTransfer, sendSolTokenTransfer, sendEvmNativeTransfer, sendSolNativeTransfer,
  readErc20Decimals, getSolanaDecimals, fetchCoinBalance,
} from "@/lib/onchain";
import { fmtUSD, fmtUSDprecise, fmtToken, fmtPrice, entityLabel, CHAIN_LABELS } from "@/lib/format";
import { CoinIcon } from "@/components/CoinIcon";
import DepositPanel from "@/components/DepositPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ContributeModal({ entity, entities, open, onClose, onSuccess, prefillAmount = null }) {
  const { wallet, connect } = useWallet();
  const config = useConfig();
  const [amount, setAmount] = useState("");
  const [inputMode, setInputMode] = useState("coin"); // "coin" | "usd"
  const [unitPrice, setUnitPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signMsg, setSignMsg] = useState("");
  const [coinKey, setCoinKey] = useState("token");
  const [payChain, setPayChain] = useState("ethereum");
  const [intent, setIntent] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);

  const isLive = config?.payment_mode === "live";
  const isToken = entity?.category === "tokens";
  const isPending = entity?.status === "pending";

  // Coin catalog
  const coins = useMemo(() => {
    const T = config?.treasuries || {};
    const ET = config?.external_treasuries || {};
    const list = [];
    // HYPE HyperCore (the listed address) can't move from EVM wallets, so the
    // token itself is never payable on Hyperliquid — native HYPE on HyperEVM is.
    if (isToken) list.push({ key: "token", label: entity.ticker, method: "token", chain: entity.chain, live: !!T[entity.chain] && entity.chain !== "hyperliquid", logo: entity.logo });
    list.push({ key: "eth", label: "ETH", method: "native", hasNetwork: true, live: !!T.ethereum || !!T.robinhood });
    list.push({ key: "bnb", label: "BNB", method: "native", chain: "bnb", live: !!T.bnb });
    list.push({ key: "sol", label: "SOL", method: "native", chain: "solana", live: !!T.solana });
    list.push({ key: "hype", label: "HYPE", method: "native", chain: "hyperliquid", live: !!T.hyperliquid });
    list.push({ key: "usdc", label: "USDC", method: "usdc", hasNetwork: true, live: true });
    list.push({ key: "usdt", label: "USDT", method: "usdt", hasNetwork: true, live: true });
    list.push({ key: "btc", label: "BTC", method: "btc", chain: "bitcoin", external: true, live: !!ET.bitcoin });
    list.push({ key: "doge", label: "DOGE", method: "doge", chain: "dogecoin", external: true, live: !!ET.dogecoin });
    list.push({ key: "trx", label: "TRX", method: "trx", chain: "tron", external: true, live: !!ET.tron });
    return list;
  }, [config, entity, isToken]);

  const coin = useMemo(() => coins.find((c) => c.key === coinKey) || coins[0], [coins, coinKey]);

  // network options for USDC / USDT
  const ethNetworks = useMemo(() => {
    const T = config?.treasuries || {};
    const supported = ["ethereum", "robinhood"];
    const live = supported.filter((chain) => T[chain]);
    return live.length ? live : supported;
  }, [config]);
  const usdcNetworks = config?.caller_payment_chains || ["ethereum", "base", "solana"];
  const usdtNetworks = useMemo(() => {
    const T = config?.treasuries || {};
    const evm = Object.keys(config?.usdt_addresses || {}).filter((c) => T[c]);
    if (config?.external_treasuries?.tron) evm.push("tron");
    return evm.length ? evm : ["ethereum"];
  }, [config]);
  const networks = coin?.key === "eth" ? ethNetworks
    : coin?.key === "usdc" ? usdcNetworks
      : coin?.key === "usdt" ? usdtNetworks : null;

  const effectiveChain = coin?.hasNetwork
    ? (networks?.includes(payChain) ? payChain : networks?.[0])
    : coin?.chain;
  const symbol = coin?.label;
  const isExternal = !!coin?.external || (coin?.key === "usdt" && payChain === "tron");
  const isStable = coin?.method === "usdc" || coin?.method === "usdt";
  const walletCapable = !isExternal;

  // live check for current selection
  const T = config?.treasuries || {};
  const ET = config?.external_treasuries || {};
  const selectionLive = coin?.hasNetwork
    ? (effectiveChain === "tron" ? !!ET.tron : !!T[effectiveChain])
    : coin?.external ? !!ET[coin.chain] : !!T[coin?.chain];

  useEffect(() => {
    if (open) {
      setAmount(""); setUnitPrice(null); setSigning(false); setSignMsg(""); setInputMode("coin");
      // Default to the token itself only when it's actually payable (its chain
      // has a live treasury AND the token can move from wallets); otherwise the
      // first live native coin, then USDC.
      const T = config?.treasuries || {};
      const tokenPayable = isToken && !!T[entity?.chain] && entity?.chain !== "hyperliquid";
      const fallbackNative = ["eth", "bnb", "sol", "hype"].find((k) => coins.find((c) => c.key === k)?.live);
      setCoinKey(tokenPayable ? "token" : (fallbackNative || "usdc"));
      setIntent(null); setCreatingIntent(false);
      if (prefillAmount) {
        // Rank-estimator hand-off: exact USD amount to take the target rank.
        setInputMode("usd");
        setAmount(String(prefillAmount));
      }
    }
  }, [open, entity, isToken, prefillAmount, config, coins]);

  // reset network default when switching to a coin with networks
  useEffect(() => {
    if (coin?.hasNetwork && networks && !networks.includes(payChain)) setPayChain(networks[0]);
  }, [coin, networks, payChain]);

  // live unit price (updates USD while typing)
  useEffect(() => {
    if (!open || !coin) return;
    if (isStable) { setUnitPrice(1); return; }
    let cancelled = false;
    setLoadingPrice(true);
    (async () => {
      try {
        if (coin.method === "token") {
          const r = await tokenLivePrice(entity.id);
          if (!cancelled) setUnitPrice(r.price_usd || entity.price_usd || 0);
        } else {
          const r = await pricePreview(symbol, 1);
          if (!cancelled) setUnitPrice(r.price_usd || 0);
        }
      } catch { if (!cancelled) setUnitPrice(coin.method === "token" ? (entity.price_usd || 0) : 0); }
      finally { if (!cancelled) setLoadingPrice(false); }
    })();
    return () => { cancelled = true; };
  }, [open, coinKey, effectiveChain, symbol, coin, isStable, entity]);

  // Wallet balance of the selected coin (read-only, no chain switch needed).
  // undefined = loading, null = not available for this coin/chain.
  const [balance, setBalance] = useState(undefined);
  useEffect(() => {
    if (!open || !coin || !wallet?.address) { setBalance(undefined); return; }
    const needSolana = effectiveChain === "solana";
    let tokenContract = null;
    if (coin.method === "usdc") tokenContract = needSolana ? config?.sol_usdc_mint : config?.usdc_addresses?.[effectiveChain];
    else if (coin.method === "usdt") tokenContract = config?.usdt_addresses?.[effectiveChain];
    else if (coin.method === "token") tokenContract = entity?.contract_address;
    if (coin.method !== "native" && !tokenContract) { setBalance(null); return; }
    let cancelled = false;
    setBalance(undefined);
    fetchCoinBalance({ method: coin.method, chain: effectiveChain, tokenContract, address: wallet.address })
      .then((b) => { if (!cancelled) setBalance(b); })
      .catch(() => { if (!cancelled) setBalance(null); });
    return () => { cancelled = true; };
  }, [open, coinKey, effectiveChain, wallet?.address, entity?.id, entity?.contract_address, coin, config]);

  const usdInputMode = inputMode === "usd" && !isStable && !!unitPrice;
  const rawNum = Number(amount);
  const validNum = amount !== "" && !isNaN(rawNum) && rawNum > 0 ? rawNum : 0;
  const coinAmount = usdInputMode ? (unitPrice ? validNum / unitPrice : 0) : validNum;
  const usdValue = coinAmount * (unitPrice != null ? unitPrice : 0);
  const sendAmount = coinAmount ? Number(coinAmount.toFixed(8)) : 0;
  // Wallet can't cover the amount (gas buffer for native coins): wallet pay
  // disables, the QR/deposit route stays available.
  const GAS_BUFFER = { solana: 0.002, ethereum: 0.001, robinhood: 0.001, bnb: 0.001, hyperliquid: 0.001 };
  const spendable = balance != null
    ? (coin.method === "native" ? Math.max(0, balance - (GAS_BUFFER[effectiveChain] ?? 0.001)) : balance)
    : null;
  const insufficient = spendable != null && coinAmount > spendable;

  const fillMax = () => {
    if (spendable == null) return;
    setInputMode("coin");
    setAmount(String(Number(spendable.toFixed(8))));
  };

  const projected = useMemo(() => {
    if (!entity || !usdValue) return null;
    const newScore = entity.total_backed_usd + usdValue;
    let rank = 1;
    (entities || []).forEach((e) => { if (e.id !== entity.id && e.total_backed_usd > newScore) rank++; });
    return { newScore, rank };
  }, [entity, usdValue, entities]);

  const currentRank = entity?.leaderboard_rank || entity?.rank;
  const nextTarget = entity?.next_target || null;
  const gapToNext = entity?.gap_to_next || null;

  const fillGapAmount = useCallback(() => {
    setInputMode("usd");
    setAmount(String(gapToNext));
  }, [gapToNext]);

  // ---- Wallet (direct sign) flow ----
  const runDemo = useCallback(async () => {
    let w = wallet;
    if (!w) { w = await connect(effectiveChain === "solana" ? "Phantom" : "MetaMask"); }
    setSignMsg("Simulating signature…");
    await new Promise((r) => setTimeout(r, 1400));
    const result = await contribute({
      entity_id: entity.id, wallet_address: w.address, amount: sendAmount,
      payment_symbol: symbol, pay_with: coin.method, chain: effectiveChain,
    });
    onSuccess(result);
  }, [wallet, connect, effectiveChain, entity, sendAmount, symbol, coin, onSuccess]);

  const runLive = useCallback(async () => {
    const needSolana = effectiveChain === "solana";
    let w = wallet;
    if (!w || w.ecosystem !== (needSolana ? "solana" : "evm") || !w.real) {
      setSignMsg("Connecting wallet…");
      w = await connect(needSolana ? "Phantom" : "MetaMask");
      if (!w.real) throw new Error(needSolana
        ? "No Solana wallet detected — install Phantom or Solflare"
        : "No EVM wallet detected — install MetaMask, Phantom (EVM) or Coinbase Wallet");
    }
    const treasury = config.treasuries?.[effectiveChain];
    if (!treasury) throw new Error("Treasury not configured for this network");
    if (w.address && String(w.address).toLowerCase() === String(treasury).toLowerCase()) {
      throw new Error("You are connected with the treasury wallet — switch to a personal wallet to contribute");
    }
    const mode = coin.method;

    setSignMsg("Confirm the transfer in your wallet…");
    let sent;
    if (mode === "native") {
      sent = needSolana
        ? await sendSolNativeTransfer({ treasury, amountHuman: sendAmount })
        : await sendEvmNativeTransfer({ chainKey: effectiveChain, treasury, amountHuman: sendAmount });
    } else {
      let token, decimals;
      if (mode === "usdc") {
        token = needSolana ? config.sol_usdc_mint : config.usdc_addresses?.[effectiveChain];
        decimals = needSolana ? 6 : await readErc20Decimals(effectiveChain, token);
      } else if (mode === "usdt") {
        token = config.usdt_addresses?.[effectiveChain];
        decimals = await readErc20Decimals(effectiveChain, token);
      } else {
        token = entity.contract_address;
        setSignMsg("Reading token details…");
        decimals = needSolana ? await getSolanaDecimals(token) : await readErc20Decimals(effectiveChain, token);
        setSignMsg("Confirm the transfer in your wallet…");
      }
      sent = needSolana
        ? await sendSolTokenTransfer({ mint: token, treasury, amountHuman: sendAmount, decimals })
        : await sendEvmTokenTransfer({ chainKey: effectiveChain, token, treasury, amountHuman: sendAmount, decimals });
    }
    const txHash = sent?.txHash || sent?.hash || sent?.signature || sent;
    // The account that actually signed in the wallet is the source of truth:
    // never trust a possibly-stale connected address for verification.
    const payer = sent?.from || w.address;

    setSignMsg("Verifying on-chain…");
    const result = await contributeOnchain({
      entity_id: entity.id, wallet_address: payer, tx_hash: txHash,
      chain: effectiveChain, pay_with: mode,
    });
    onSuccess(result);
  }, [wallet, connect, config, effectiveChain, coin, entity, sendAmount, onSuccess]);

  const handleWallet = useCallback(async () => {
    if (!entity || !usdValue) return;
    setSigning(true);
    try {
      if (isLive) await runLive();
      else await runDemo();
    } catch (e) {
      const code = e?.code;
      if (code === 4001) toast.error("Transaction rejected in wallet");
      else toast.error(e.response?.data?.detail || e.message || "Transaction failed");
    } finally { setSigning(false); }
  }, [entity, usdValue, isLive, runLive, runDemo]);

  // ---- Send-to-address (payment intent) flow ----
  const handleDeposit = useCallback(async () => {
    if (!entity || !sendAmount) return;
    setCreatingIntent(true);
    try {
      const it = await createPaymentIntent({
        entity_id: entity.id, pay_with: coin.method, chain: effectiveChain,
        amount: sendAmount, wallet_address: wallet?.address || null,
      });
      setIntent(it);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create payment request");
    } finally { setCreatingIntent(false); }
  }, [entity, sendAmount, coin, effectiveChain, wallet]);

  if (!entity) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !signing && onClose()}>
      <DialogContent className="max-w-md gap-0 border-white/10 bg-[#0a0a0a] p-0 pb-safe [&>button]:hidden" data-testid="contribute-modal">
        <DialogTitle className="sr-only">Contribute to {entity?.ticker || entity?.name || "this profile"}</DialogTitle>
        <DialogDescription className="sr-only">Choose a payment method and amount to back this leaderboard profile.</DialogDescription>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0a0a0a] px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Back this {isToken ? "token" : "KOL"}</div>
            <div className="font-display text-xl font-black tracking-tight">{entityLabel(entity)}</div>
          </div>
          <button onClick={() => !signing && onClose()} className="text-zinc-500 hover:text-white" data-testid="contribute-close">
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        {signing ? (
          <div className="flex flex-col items-center gap-4 px-6 py-14 text-center" data-testid="signing-state">
            <Loader2 className="h-10 w-10 animate-spin text-white" strokeWidth={1.5} />
            <div className="font-display text-lg font-bold">{signMsg || "Processing…"}</div>
            <div className="text-sm text-zinc-500">{isLive ? "Real transaction — do not close this window" : "Signing & confirming (demo)"}</div>
          </div>
        ) : intent ? (
          <DepositPanel
            intent={intent}
            onConfirmed={(r) => {
              setIntent(null);
              toast.success(`Payment received — ${fmtUSDprecise(r?.contribution?.usd_value || 0)} credited to ${entityLabel(entity)}! 🚀`);
              onSuccess(r);
            }}
            onBack={() => setIntent(null)}
          />
        ) : (
          <div className="space-y-5 px-5 py-5">
            {isPending && (
              <div className="rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-2.5 text-[11px] text-gold/90" data-testid="activation-banner">
                🆕 <b>{entityLabel(entity)}</b> isn't listed yet. Send at least <b>$1</b> in {symbol} to activate it and put it on the leaderboard.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Current rank</div>
                <div className="font-mono text-lg font-bold" data-testid="current-rank">{isPending || !currentRank ? "N/A" : `#${currentRank}`}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Current score</div>
                <div className="font-mono text-lg font-bold">{fmtUSD(entity.total_backed_usd)}</div>
              </div>
            </div>

            {!isPending && nextTarget && gapToNext && (
              <button
                type="button"
                data-testid="flip-next-banner"
                onClick={fillGapAmount}
                className="flex w-full items-center justify-between rounded-xl border border-gold/30 bg-gold/[0.07] px-4 py-3 text-left transition-colors hover:bg-gold/[0.12]"
              >
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-gold">
                    ⚔️ Flip {nextTarget.label} for {fmtUSD(gapToNext)}
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-400">
                    {fmtUSD(gapToNext)} more takes {entityLabel(entity)} to #{Math.max((currentRank || 2) - 1, 1)} — tap to fill the exact amount
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-gold" />
              </button>
            )}

            {/* Pay-with coin selector */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pay with</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {coins.map((c) => (
                  <button
                    key={c.key} data-testid={`paywith-${c.key}`} disabled={!c.live}
                    onClick={() => setCoinKey(c.key)}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                      coinKey === c.key ? "border-white bg-white text-black"
                      : c.live ? "border-white/15 bg-white/[0.02] text-zinc-300 hover:border-white/40"
                      : "cursor-not-allowed border-white/5 bg-white/[0.01] text-zinc-600 opacity-50"}`}
                  >
                    <CoinIcon symbol={c.key === "token" ? c.label : c.label} logo={c.logo} size={16} />
                    {c.label}{!c.live ? " · soon" : ""}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[10px] text-zinc-600">Back {entityLabel(entity)} with the token itself or a blue-chip coin — every contribution is valued in USD, manipulation-proof.</p>
            </div>

            {/* Network selector for USDC / USDT */}
            {coin?.hasNetwork && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pay on network</label>
                <Select value={payChain} onValueChange={setPayChain}>
                  <SelectTrigger data-testid="paychain-select" className="mt-1 border-white/15 bg-black">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0a0a0a]">
                    {networks.map((c) => <SelectItem key={c} value={c}>{CHAIN_LABELS[c] || c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {coin?.method === "token" && (
              <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] text-zinc-400">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" strokeWidth={1.5} />
                <div>
                  <span className="text-zinc-300">Verify the contract</span> on {CHAIN_LABELS[entity.chain] || entity.chain}:
                  <div className="mt-0.5 break-all font-mono text-[10px] text-zinc-500">{entity.contract_address}</div>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {usdInputMode ? "Amount in USD to contribute" : `Amount of ${symbol} to contribute`}
                </label>
                {!isStable && !!unitPrice && (
                  <div className="flex rounded-full border border-white/10 bg-white/[0.02] p-1" data-testid="input-mode-toggle">
                    <button data-testid="input-mode-coin" onClick={() => setInputMode("coin")}
                      className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase transition-colors ${inputMode === "coin" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>{symbol}</button>
                    <button data-testid="input-mode-usd" onClick={() => setInputMode("usd")}
                      className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase transition-colors ${inputMode === "usd" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>USD</button>
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-white/15 bg-black px-4 py-3 focus-within:border-white/40">
                {usdInputMode && <span className="font-mono text-2xl font-bold text-zinc-500">$</span>}
                <input
                  data-testid="contribute-amount-input" type="number" min="0" value={amount}
                  onChange={(e) => setAmount(e.target.value)} placeholder="0" autoFocus
                  className="w-full bg-transparent font-mono text-2xl font-bold outline-none placeholder:text-zinc-700"
                />
                <span className="font-mono text-sm font-bold text-zinc-500">{usdInputMode ? "USD" : symbol}</span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-zinc-500" data-testid="live-unit-price">
                  {isStable ? `1 ${symbol} = $1.00`
                    : loadingPrice ? "Fetching live price…"
                    : unitPrice ? `1 ${symbol} ≈ ${fmtPrice(unitPrice)}` : "—"}
                </span>
                <span className="font-mono text-sm font-bold text-white" data-testid="contribute-usd-value">
                  {usdInputMode ? `≈ ${fmtToken(coinAmount)} ${symbol}` : `≈ ${fmtUSDprecise(usdValue)}`}
                </span>
              </div>
              {wallet?.real && (
                <div className="mt-1.5 flex items-center justify-between text-[11px]" data-testid="wallet-balance-row">
                  <span className="text-zinc-500">
                    Wallet balance:{" "}
                    {balance === undefined ? "…" : balance == null
                      ? <span className="text-zinc-600">n/a</span>
                      : <span className={insufficient ? "text-negative" : "text-zinc-300"}>{fmtToken(balance)} {symbol}</span>}
                  </span>
                  {spendable != null && spendable > 0 && (
                    <button onClick={fillMax} data-testid="fill-max-button"
                      className="text-[10px] font-bold uppercase tracking-wider text-gold hover:text-gold/80">
                      Max
                    </button>
                  )}
                </div>
              )}
            </div>

            {projected && (
              <div className="space-y-2 rounded-xl border border-gold/20 bg-gold/[0.04] px-4 py-3" data-testid="contribute-projection">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">{entityLabel(entity)} score</span>
                  <span className="flex items-center gap-2 font-mono font-bold">
                    {fmtUSD(entity.total_backed_usd)} <ArrowRight className="h-3 w-3 text-gold" />
                    <span className="text-gold">{fmtUSD(projected.newScore)}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Estimated new rank</span>
                  <span className="flex items-center gap-2 font-mono font-bold">
                    {isPending || !currentRank ? "N/A" : `#${currentRank}`} <ArrowRight className="h-3 w-3 text-gold" />
                    <span className="text-gold">#{projected.rank}</span>
                  </span>
                </div>
              </div>
            )}

            {insufficient && (
              <div className="rounded-xl border border-negative/30 bg-negative/[0.06] px-4 py-2.5 text-[11px] text-negative" data-testid="insufficient-balance">
                Not enough {symbol} in your wallet ({fmtToken(balance ?? 0)} available, minus network fee) —
                reduce the amount, or use <b>"Send from exchange"</b> below instead.
              </div>
            )}

            {!selectionLive ? (
              <p className="rounded-xl border border-gold/25 bg-gold/[0.05] px-4 py-3 text-center text-[12px] text-gold/90" data-testid="chain-not-live">
                Backing with {symbol} on {CHAIN_LABELS[effectiveChain] || effectiveChain} isn't enabled yet — a treasury for this network is required.
              </p>
            ) : (
              <div className="space-y-2">
                {walletCapable && (
                  <button
                    data-testid="contribute-confirm-button" disabled={!usdValue || insufficient} onClick={handleWallet}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Wallet className="h-4 w-4" /> Pay with wallet
                  </button>
                )}
                <button
                  data-testid="deposit-intent-button" disabled={!usdValue || creatingIntent} onClick={handleDeposit}
                  className={`flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-display text-sm font-black uppercase tracking-wide transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 ${
                    walletCapable ? "border border-white/20 bg-transparent text-white hover:bg-white/5" : "bg-white text-black hover:bg-zinc-200"}`}
                >
                  {creatingIntent ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                  Send from exchange / any wallet
                </button>
              </div>
            )}

            <p className="text-center text-[10px] text-zinc-600">
              {isLive
                ? "Live mode — real funds are transferred to the treasury and verified on-chain."
                : "Demo mode — wallet payments are simulated. \"Send from exchange\" detects real transfers."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
